import dagre from "dagre";

// ── Layout constants (kept from original for visual consistency) ──────────────
const COLUMN_GAP = 132;
const ROW_GAP = 38;
const ROOT_GAP = 72;
const CLAIM_CLUSTER_CLEARANCE = 24;
const CLAIM_VERTICAL_STEP = 28;
const CLAIM_GAP_X = 28;
const CLAIM_GAP_Y = 18;

// ── Rectangle overlap helper ─────────────────────────────────────────────────
const rectsOverlap = (a, b, padding = 0) => {
  return !(
    a.x + a.width + padding <= b.x ||
    b.x + b.width + padding <= a.x ||
    a.y + a.height + padding <= b.y ||
    b.y + b.height + padding <= a.y
  );
};

// ── Primary DAG layout via dagre ─────────────────────────────────────────────
function layoutPrimaryNodes(primaryNodes, primaryEdges, options = {}) {
  const positions = {};
  const nodeById = new Map(primaryNodes.map((n) => [n.id, n]));
  const childrenById = new Map();
  const parentById = new Map();

  primaryNodes.forEach((n) => childrenById.set(n.id, []));
  primaryEdges.forEach((e) => {
    if (childrenById.has(e.from) && childrenById.has(e.to)) {
      childrenById.get(e.from).push(e.to);
      if (!parentById.has(e.to)) parentById.set(e.to, e.from);
    }
  });

  const orderedNodeIds = primaryNodes.map((n) => n.id);
  const nodeSet = new Set(orderedNodeIds);
  const roots = orderedNodeIds.filter((id) => !parentById.has(id));
  const orderedRoots = roots.length ? roots : orderedNodeIds.slice(0, 1);

  // ── Lineage edge handling ────────────────────────────────────────────────
  const lineageEdges = Array.isArray(options?.lineageEdges) ? options.lineageEdges : [];
  const lineageEdgeSet = new Set(lineageEdges.map((e) => `${e.from}:${e.to}`));
  const lineageTargets = lineageEdges
    .map((e) => String(e?.to ?? "").trim())
    .filter((id) => nodeSet.has(id));

  // Collect descendants of lineage targets (for depth-offset computation)
  const collectDescendants = (nodeId, lineage = new Set()) => {
    if (!nodeSet.has(nodeId) || lineage.has(nodeId)) return [];
    const next = new Set(lineage);
    next.add(nodeId);
    const descendants = [nodeId];
    (childrenById.get(nodeId) ?? []).forEach((childId) => {
      descendants.push(...collectDescendants(childId, next));
    });
    return descendants;
  };

  const lineageDepthOffsetById = new Map();
  lineageTargets.forEach((targetId) => {
    collectDescendants(targetId).forEach((descId) => {
      lineageDepthOffsetById.set(descId, (lineageDepthOffsetById.get(descId) ?? 0) + 1);
    });
  });

  // ── Build dagre graph ────────────────────────────────────────────────────
  const g = new dagre.graphlib.Graph();
  g.setGraph({
    rankdir: "LR",
    nodesep: ROW_GAP,
    ranksep: COLUMN_GAP,
    ranker: "network-simplex", // better for DAGs with cross-connections
  });
  g.setDefaultEdgeLabel(() => ({}));

  primaryNodes.forEach((node) => {
    g.setNode(node.id, {
      width: node.width ?? 260,
      height: node.height ?? 150,
    });
  });

  primaryEdges.forEach((edge) => {
    const isLineage = lineageEdgeSet.has(`${edge.from}:${edge.to}`);
    g.setEdge(edge.from, edge.to, {
      weight: isLineage ? 1000 : 1, // prioritize lineage edges
    });
  });

  // ── Run dagre layout ─────────────────────────────────────────────────────
  dagre.layout(g);

  // Extract positions (dagre centers nodes; convert to top-left)
  const dagrePositions = new Map();
  g.nodes().forEach((nodeId) => {
    const dn = g.node(nodeId);
    dagrePositions.set(nodeId, {
      x: dn.x - ((dn.width ?? 260) / 2),
      y: dn.y - ((dn.height ?? 150) / 2),
    });
  });

  // Assign positions
  primaryNodes.forEach((node) => {
    positions[node.id] = dagrePositions.get(node.id) ?? { x: 0, y: 0 };
  });

  // Fallback for any nodes dagre didn't place (shouldn't happen, but safe)
  orderedNodeIds.forEach((nodeId) => {
    if (!Number.isFinite(positions[nodeId]?.x) || !Number.isFinite(positions[nodeId]?.y)) {
      positions[nodeId] = { x: 0, y: 0 };
    }
  });

  // ── Post-layout overlap resolution (single pass, spatial-grid optimized) ──
  // Dagre handles most overlaps. This pass only fixes edge cases (disconnected
  // components, very wide nodes) using a spatial grid to avoid O(n²) checks.
  const CELL_SIZE = 200;
  const grid = new Map();
  const occupiedRects = orderedNodeIds.map((nodeId) => {
    const node = nodeById.get(nodeId) ?? {};
    const pos = positions[nodeId] ?? { x: 0, y: 0 };
    const rect = {
      x: pos.x,
      y: pos.y,
      width: node.width ?? 260,
      height: node.height ?? 150,
      nodeId,
    };
    // Insert into spatial grid
    const minCol = Math.floor(pos.x / CELL_SIZE);
    const maxCol = Math.floor((pos.x + rect.width) / CELL_SIZE);
    const minRow = Math.floor(pos.y / CELL_SIZE);
    const maxRow = Math.floor((pos.y + rect.height) / CELL_SIZE);
    for (let r = minRow; r <= maxRow; r++) {
      for (let c = minCol; c <= maxCol; c++) {
        const key = `${r},${c}`;
        if (!grid.has(key)) grid.set(key, []);
        grid.get(key).push(rect);
      }
    }
    return rect;
  });

  // Check only spatially adjacent cells for overlaps
  for (let pass = 0; pass < 2; pass++) {
    let changed = false;
    const checked = new Set();
    grid.forEach((rects) => {
      rects.forEach((a) => {
        rects.forEach((b) => {
          if (a.nodeId >= b.nodeId) return;
          const pairKey = `${a.nodeId}-${b.nodeId}`;
          if (checked.has(pairKey)) return;
          checked.add(pairKey);
          if (!rectsOverlap(a, b, 0)) return;

          if (positions[a.nodeId].x <= positions[b.nodeId].x) {
            const pushAmount = a.x + a.width + 12 - b.x;
            if (pushAmount > 0) {
              b.x += pushAmount;
              b.y = a.y;
              positions[b.nodeId] = { x: b.x, y: b.y };
              changed = true;
            }
          } else {
            const pushAmount = b.x + b.width + 12 - a.x;
            if (pushAmount > 0) {
              a.x += pushAmount;
              a.y = b.y;
              positions[a.nodeId] = { x: a.x, y: a.y };
              changed = true;
            }
          }
        });
      });
    });
    if (!changed) break;
  }

  return { positions, occupiedRects, childrenById, nodeSet, orderedNodeIds, nodeById };
}

// ── Claim cluster layout ─────────────────────────────────────────────────────
function layoutClaimClusters(
  claimEdges,
  positions,
  nodeById,
  childrenById,
  nodeSet,
  occupiedRects,
) {
  const claimsByParentId = new Map();
  claimEdges.forEach((edge) => {
    const existing = claimsByParentId.get(edge.from) ?? [];
    existing.push(edge.to);
    claimsByParentId.set(edge.from, existing);
  });

  // Measure how far right a subtree extends (including its claim clusters)
  const primarySubtreeRightById = new Map();
  const measurePrimarySubtreeRight = (nodeId, lineage = new Set()) => {
    if (primarySubtreeRightById.has(nodeId)) return primarySubtreeRightById.get(nodeId);
    if (lineage.has(nodeId)) {
      const node = nodeById.get(nodeId) ?? {};
      const pos = positions[nodeId] ?? { x: 0 };
      return pos.x + (node.width ?? 260);
    }
    const node = nodeById.get(nodeId) ?? {};
    const pos = positions[nodeId] ?? { x: 0 };
    let maxRight = pos.x + (node.width ?? 260);
    const next = new Set(lineage);
    next.add(nodeId);
    (childrenById.get(nodeId) ?? []).forEach((childId) => {
      if (!nodeSet.has(childId)) return;
      maxRight = Math.max(maxRight, measurePrimarySubtreeRight(childId, next));
    });
    primarySubtreeRightById.set(nodeId, maxRight);
    return maxRight;
  };

  const claimClusterRightByParentId = new Map();
  const measureOccupiedSubtreeRight = (nodeId, lineage = new Set()) => {
    if (lineage.has(nodeId)) {
      return Math.max(
        measurePrimarySubtreeRight(nodeId),
        claimClusterRightByParentId.get(nodeId) ?? -Infinity,
      );
    }
    let maxRight = Math.max(
      measurePrimarySubtreeRight(nodeId),
      claimClusterRightByParentId.get(nodeId) ?? -Infinity,
    );
    const next = new Set(lineage);
    next.add(nodeId);
    (childrenById.get(nodeId) ?? []).forEach((childId) => {
      if (!nodeSet.has(childId)) return;
      maxRight = Math.max(maxRight, measureOccupiedSubtreeRight(childId, next));
    });
    return maxRight;
  };

  // Sort claim clusters by parent X position (rightmost / deepest first)
  // so that deeper clusters are placed first and shallower ones account for them.
  Array.from(claimsByParentId.entries())
    .sort((left, right) => {
      const leftX = (positions[left[0]] ?? { x: 0 }).x;
      const rightX = (positions[right[0]] ?? { x: 0 }).x;
      return rightX - leftX;
    })
    .forEach(([parentId, claimIds]) => {
      const parentNode = nodeById.get(parentId);
      const parentPosition = positions[parentId] ?? { x: 0, y: 0 };
      if (!parentNode) return;

      const columnCount = claimIds.length <= 4 ? 2 : 3;
      const claimRows = Math.ceil(claimIds.length / columnCount);
      const claimDimensions = claimIds.map((claimId) => {
        const claimNode = nodeById.get(claimId) ?? {};
        return {
          width: claimNode.width ?? 240,
          height: claimNode.height ?? 132,
        };
      });
      const sampleClaimHeight = claimDimensions[0]?.height ?? 132;
      const columnWidth = claimDimensions.reduce(
        (maxWidth, claim) => Math.max(maxWidth, claim.width),
        240,
      );
      const clusterWidth =
        columnCount * columnWidth + CLAIM_GAP_X * Math.max(0, columnCount - 1);
      const clusterHeight =
        claimDimensions.reduce(
          (maxHeight, claim) => Math.max(maxHeight, claim.height),
          sampleClaimHeight,
        ) * claimRows +
        CLAIM_GAP_Y * Math.max(0, claimRows - 1);

      const parentCenterY = parentPosition.y + (parentNode.height ?? 150) / 2;
      const subtreeRight = measureOccupiedSubtreeRight(parentId);
      const clusterX = subtreeRight + 56;
      const preferredTop = parentCenterY - clusterHeight / 2;

      // Spiral search for a Y position that doesn't overlap occupied rects
      const findAvailableClusterTop = (clusterRect) => {
        const maxAttempts = 80;
        for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
          const direction = attempt % 2 === 0 ? 1 : -1;
          const magnitude = Math.ceil(attempt / 2);
          const candidateY = clusterRect.y + direction * magnitude * CLAIM_VERTICAL_STEP;
          const candidateRect = { ...clusterRect, y: candidateY };
          const overlaps = occupiedRects.some((rect) =>
            rectsOverlap(candidateRect, rect, CLAIM_CLUSTER_CLEARANCE),
          );
          if (!overlaps) return candidateY;
        }
        return clusterRect.y;
      };

      const clusterTop = findAvailableClusterTop({
        x: clusterX,
        y: preferredTop,
        width: clusterWidth,
        height: clusterHeight,
      });

      let clusterRight = subtreeRight;
      claimIds.forEach((claimId, index) => {
        const claimNode = nodeById.get(claimId) ?? {};
        const claimWidth = claimNode.width ?? 240;
        const claimHeight = claimNode.height ?? 132;
        const column = Math.floor(index / claimRows);
        const row = index % claimRows;
        const claimX = clusterX + column * (columnWidth + CLAIM_GAP_X);
        positions[claimId] = {
          x: claimX,
          y: clusterTop + row * (claimHeight + CLAIM_GAP_Y),
        };
        clusterRight = Math.max(clusterRight, claimX + claimWidth);
      });
      claimClusterRightByParentId.set(parentId, clusterRight);
      occupiedRects.push({
        x: clusterX,
        y: clusterTop,
        width: clusterWidth,
        height: clusterHeight,
      });
    });
}

// ── Public API (synchronous — dagre is fast enough for typical graph sizes) ──
export function computeTreeLayout(viz, nodes, edges, ready, options = {}) {
  const positions = {};
  if (!nodes?.length) {
    nodes?.forEach((node) => {
      positions[node.id] = { x: 0, y: 0 };
    });
    return positions;
  }

  const nodeById = new Map(nodes.map((node) => [node.id, node]));
  const claimNodeIds = new Set(
    nodes.filter((n) => String(n?.lifecycleStatus ?? "").toLowerCase() === "claim").map((n) => n.id),
  );
  const primaryNodes = nodes.filter((n) => !claimNodeIds.has(n.id));
  const primaryEdges = edges.filter((e) => !claimNodeIds.has(e.to) && !claimNodeIds.has(e.from));
  const claimEdges = edges.filter((e) => claimNodeIds.has(e.to) && !claimNodeIds.has(e.from));

  // Layout primary nodes with dagre
  const {
    positions: primaryPositions,
    occupiedRects,
    childrenById,
    nodeSet,
    nodeById: primaryNodeById,
  } = layoutPrimaryNodes(primaryNodes, primaryEdges, options);

  // Merge into shared positions map
  Object.assign(positions, primaryPositions);

  // Layout claim clusters to the right of their parents
  if (claimEdges.length > 0) {
    layoutClaimClusters(claimEdges, positions, primaryNodeById, childrenById, nodeSet, occupiedRects);
  }

  return positions;
}

// ── Bezier link point computation (unchanged from original) ──────────────────
export function getLinkPoints(fromId, toId, positions, nodeLookup, options = {}) {
  const fromNode = nodeLookup[fromId] ?? {};
  const toNode = nodeLookup[toId] ?? {};
  const fromPosition = positions[fromId] ?? { x: 0, y: 0 };
  const toPosition = positions[toId] ?? { x: 0, y: 0 };
  const fromWidth = fromNode.width ?? 260;
  const fromHeight = fromNode.height ?? 150;
  const toHeight = toNode.height ?? 150;
  const sourceOffsetX = Number(options?.sourceOffsetX ?? 0);
  const sourceOffsetY = Number(options?.sourceOffsetY ?? 0);
  const targetOffsetX = Number(options?.targetOffsetX ?? 0);
  const targetOffsetY = Number(options?.targetOffsetY ?? 0);
  const controlOffsetX = Number(options?.controlOffsetX ?? 0);
  const controlOffsetY = Number(options?.controlOffsetY ?? 0);

  const startX = fromPosition.x + fromWidth + sourceOffsetX;
  const startY = fromPosition.y + fromHeight / 2 + sourceOffsetY;
  const endX = toPosition.x + targetOffsetX;
  const endY = toPosition.y + toHeight / 2 + targetOffsetY;

  const deltaX = endX - startX;
  const cp1X = startX + deltaX * 0.82 + controlOffsetX;
  const cp1Y = startY + controlOffsetY;
  const cp2X = startX + deltaX * 0.18 + controlOffsetX;
  const cp2Y = endY + controlOffsetY;

  return [startX, startY, cp1X, cp1Y, cp2X, cp2Y, endX, endY];
}
